import BigNumber from 'bignumber.js';

const SECONDS_PER_DAY = BigInt(86_400);
const DEFAULT_MAX_PERCENTAGE = BigInt(10_000);

export type StakingWithdrawalQuoteInput = {
  amount: string;
  claimable: string;
  unlockTimestamp: string;
  currentTimestamp: number;
  earlyUnlockFeePerDay: string;
  maxPercentage?: string;
};

export type StakingWithdrawalQuote = {
  daysLeft: string;
  penalty: string;
  principalReceivable: string;
  rewardsClaimed: string;
  totalReceivable: string;
  withdrawFeePercentage: string;
};

const toBigInt = (value: string | number) => {
  try {
    return BigInt(value);
  } catch {
    return BigInt(0);
  }
};

export const calculateStakingWithdrawalQuote = ({
  amount,
  claimable,
  unlockTimestamp,
  currentTimestamp,
  earlyUnlockFeePerDay,
  maxPercentage = DEFAULT_MAX_PERCENTAGE.toString(),
}: StakingWithdrawalQuoteInput): StakingWithdrawalQuote => {
  const amountValue = toBigInt(amount);
  const rewardsClaimed = toBigInt(claimable);
  const unlockTime = toBigInt(unlockTimestamp);
  const currentTime = toBigInt(currentTimestamp);
  const feePerDay = toBigInt(earlyUnlockFeePerDay);
  const percentageDenominator = toBigInt(maxPercentage);
  const secondsUntilUnlock =
    unlockTime > currentTime ? unlockTime - currentTime : BigInt(0);

  const calculatedPenalty =
    amountValue > BigInt(0) &&
    secondsUntilUnlock > BigInt(0) &&
    feePerDay > BigInt(0) &&
    percentageDenominator > BigInt(0)
      ? (amountValue * secondsUntilUnlock * feePerDay) /
        SECONDS_PER_DAY /
        percentageDenominator
      : BigInt(0);
  const penalty =
    calculatedPenalty < amountValue ? calculatedPenalty : amountValue;
  const principalReceivable = amountValue - penalty;
  const withdrawFeePercentage =
    percentageDenominator > BigInt(0)
      ? new BigNumber(feePerDay.toString())
          .multipliedBy(100)
          .dividedBy(percentageDenominator.toString())
          .toFixed()
      : '0';

  return {
    daysLeft: secondsUntilUnlock.toString(),
    penalty: penalty.toString(),
    principalReceivable: principalReceivable.toString(),
    rewardsClaimed: rewardsClaimed.toString(),
    totalReceivable: (principalReceivable + rewardsClaimed).toString(),
    withdrawFeePercentage,
  };
};
